import {
    Positionable,
    SimpleReelModel,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleReelModelSymbolPosition
} from "../../core/reel/simplereel/SimpleReelModel";
import {DasUBootSymbol, DasUBootSymbolStates} from "./DasUBootSymbol";
import {DasUBoot} from "../DasUBoot";
import {SymbolUtils} from "../utils/SymbolUtils";
import {gsap} from "gsap";
import {ReelDarkenPlate} from "../components/ReelDarkenPlate";
import {DasUBootReelSet} from "../server/DasUBootReelSet";
import {DasUBootStatics} from "../utils/DasUBootStatics";
import {GraphicsPool} from "../utils/GraphicsPool";
import {GlowFrame} from "./symbols/symbolparts/GlowFrame";
import {DasUBootRoundType} from "../server/DasUBootData";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

/**
 * Created by jonas on 2021-02-10.
 */

export class DasUBootReel extends PIXI.Container implements SimpleReelModelDelegate {
    public model:SimpleReelModel;
    public normalLayer:PIXI.Container;
    public normalOverLayer:PIXI.Container;
    public winLayer:PIXI.Container;
    private darkenPlate:ReelDarkenPlate;
    public reelId:number;
    private spinAnimation:gsap.core.Timeline;
    public reelMask:PIXI.Graphics;
    private glowFrame:GlowFrame;

    private isGrown:boolean = false;
    private specialSymbols = ["W4","S","B","T","P"];
    private currentReelSet:string[][];


    constructor(id:number) {
        super();

        this.name = "Reel " + id;

        this.reelId = id;
        this.winLayer = new PIXI.Container();
        this.winLayer.name = "WinLayer Reel " + id;

        this.winLayer.position.set(DasUBootStatics.reelPositions[id].x, DasUBootStatics.reelPositions[id].y);
        this.position.set(DasUBootStatics.reelPositions[id].x, DasUBootStatics.reelPositions[id].y);

        this.reelMask = new PIXI.Graphics();
        this.reelMask.name = "ReelMask";
        this.reelMask.beginFill();
        this.reelMask.drawRect(0, 0, DasUBootStatics.symbolWidth, DasUBootStatics.symbolHeight * DasUBootStatics.defaultGameAreaSize[id]);

        this.addChild(this.reelMask);
        this.mask = this.reelMask;

        this.normalLayer = new PIXI.Container();
        this.normalLayer.name = "NormalLayer Reel " + id;
        this.addChild(this.normalLayer);

        this.normalOverLayer = new PIXI.Container();
        this.normalOverLayer.name = "normalOverLayer Reel " + id;
        this.addChild(this.normalOverLayer);

        this.darkenPlate = new ReelDarkenPlate();
        this.darkenPlate.name = "darkenPlate";
        this.darkenPlate.position.set(0, 0);
        this.darkenPlate.setReelSize(DasUBootStatics.symbolWidth, DasUBootStatics.symbolHeight * DasUBootStatics.defaultGameAreaSize[id]);
        this.addChild(this.darkenPlate);


        this.model = new SimpleReelModel(id, this, true);
        this.changeReelSet(DasUBoot.model.data.roundType);
        this.model.illegalVisualPaddingSymbols = ["S","W4","P","B","BB", "T", "M"];
        this.model.stepDuration = DasUBootStatics.stepDuration;
        this.model.stepSize = DasUBootStatics.symbolHeight;
        this.model.paddingConfig.visualPaddingBefore = 1;
        this.model.paddingConfig.paddingBefore = 3;
        this.model.paddingConfig.visibleSymbols = DasUBootStatics.defaultGameAreaSize[id];
        this.model.paddingConfig.paddingAfter = 4 - DasUBootStatics.defaultGameAreaSize[id] + 3;
        this.model.paddingConfig.visualPaddingAfter = 1;

        this.model.init(DasUBoot.model.clientData.reels[id].concat());

        this.model.events?.on(SimpleReelModelState.BOUNCE, (data:SimpleReelModelEventData) => {
            this.stop();
            //Switches state of all symbols from spin state to normal, when the symbols bounce.
            for (let symbolData of this.model.symbols) {

                (<DasUBootSymbol>symbolData.positionable).getStateAnimation(DasUBootSymbolStates.LANDING);
            }
        });
        this.model.events?.on(SimpleReelModelState.SPIN, (data:SimpleReelModelEventData) => {
            this.start();
            //Switches state of all symbols from whatever state to spin when reel wind up is done and the spinning starts.
            for (let symbolData of this.model.symbols) {
                (<DasUBootSymbol>symbolData.positionable).getStateAnimation(DasUBootSymbolStates.SPIN);
            }
        });


        this.glowFrame = DasUBoot.graphicsPool.getGenericPool(GraphicsPool.SYMBOL_FRAME).withdraw();
        this.glowFrame.pivot.set(72, 72);
        this.glowFrame.position.set(72, 72);

    }

    public changeReelSet(mode:DasUBootRoundType){
        let reelSet = DasUBootReelSet.DEFAULT_REEL_SET;
        switch (mode){
            case DasUBootRoundType.FREE_SPIN:
                reelSet = DasUBootReelSet.FREESPIN_REELSET;
                break;
            case DasUBootRoundType.SUPER_FREE_SPIN:
                reelSet = DasUBootReelSet.SUPER_FREESPIN_REELSET;
                break;
        }

        if (reelSet == this.currentReelSet){
            return
        }
        this.currentReelSet = reelSet;
        this.model.setReelSet(reelSet[this.reelId])
    }

    public growReel(grow:boolean, duration:number = 2):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        const symbols = this.model.symbols;
        const symbolScaling = gsap.timeline();
        const customEase:any = "none";
        const scale = grow ? 0.75 : 1;

        if (grow) {
            //This removes the symbol before we do the rescale so they dont show up when we extend the reel.
            this.removeIllegalSymbolsOutsideOfVisibleReelArea();
        }

        for (let symbol of symbols) {
            symbolScaling.add((<DasUBootSymbol>symbol.positionable).resize(scale, 0, duration, customEase), 0);
        }

        if (grow) {
            tl.add([
                symbolScaling,
                this.model.resize(DasUBootStatics.symbolPeriscopeHeight, duration, customEase),
                gsap.to(this.normalLayer, {duration:duration, pixi:{scaleY:1, positionY:-288}}),
                gsap.to(this.normalOverLayer, {duration:duration, pixi:{scaleY:1, positionY:-288}}),
                gsap.to(this.winLayer, {duration:duration, pixi:{scaleY:1, positionY:-288}}),
                gsap.to(this._mask, {duration:duration, pixi:{scaleY:1.5, positionY:-288}})
            ]);
            tl.add(() => {
                this.isGrown = true;
                this.model.updatePaddingConfig({
                    visualPaddingBefore:7,
                    paddingBefore:1,
                    visibleSymbols:8,
                    paddingAfter:1,
                    visualPaddingAfter:7
                });
            })
        } else {
            tl.add([
                symbolScaling,
                this.model.resize(DasUBootStatics.symbolHeight, duration, customEase),
                gsap.to(this.normalLayer, {duration:duration, pixi:{scaleY:1, positionY:0}}),
                gsap.to(this.normalOverLayer, {duration:duration, pixi:{scaleY:1, positionY:0}}),
                gsap.to(this.winLayer, {duration:duration, pixi:{scaleY:1, positionY:0}}),
                gsap.to(this._mask, {duration:duration, pixi:{scaleY:1, positionY:0}})
            ]);
            tl.add(() => {
                this.isGrown = false;
                this.model.updatePaddingConfig({
                    visualPaddingBefore:1,
                    paddingBefore:3,
                    visibleSymbols:4,
                    paddingAfter:3,
                    visualPaddingAfter:1
                });
            })
        }

        return tl;
    }

    public getShowNearWin(repeat:number = 5):gsap.core.Timeline {
        this.glowFrame.tint = DasUBootStatics.nearWinTint;
        if (this.isGrown){
            this.glowFrame.frameSize.set(144, 144 * 6);
        }else {
            this.glowFrame.frameSize.set(144, 144 * DasUBootStatics.defaultGameAreaSize[this.reelId]);
        }

        const tl:gsap.core.Timeline = gsap.timeline();

        tl.add(this.glowFrame.hide());
        tl.add(()=>DasUBoot.reelView.reelFrame.playAvalancheNearWin(this.reelId));
        tl.add(() => {this.winLayer.addChild(this.glowFrame)});
        tl.add(this.glowFrame.show(0.1));
        for (let i:number = 0; i < repeat; i++){
            tl.add(this.glowFrame.getAttention(true));
        }
      //  tl.add(this.glowFrame.hide(0.1));
        return tl;
    }

    public getShowPeriscopeNearWin(repeat:number = 5):gsap.core.Timeline {
        this.glowFrame.tint = DasUBootStatics.periscopeNearWinFrameTint;
        if (this.isGrown){
            this.glowFrame.frameSize.set(144, 144 * 6);
        }else {
            this.glowFrame.frameSize.set(144, 144 * DasUBootStatics.defaultGameAreaSize[this.reelId]);
        }

        const tl:gsap.core.Timeline = gsap.timeline();

        tl.add(this.glowFrame.hide());
        tl.add(() => {this.normalOverLayer.addChild(this.glowFrame)});
        tl.add(this.glowFrame.show(0.1));
        tl.add(()=>this.stop());
        for (let i:number = 0; i < repeat; i++){
            tl.add(this.glowFrame.getAttention(true));
        }
        //tl.add(this.glowFrame.hide(0.1));
        return tl;
    }

    public getHideNearWin():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(()=>DasUBoot.reelView.reelFrame.stopAvalancheNearWin(this.reelId));
        tl.add(this.glowFrame.hide(0.1));
        tl.add(()=>{
            if (this.glowFrame.parent) {
                this.glowFrame.position.set(72, 72);
                this.glowFrame.parent.removeChild(this.glowFrame);
            }
        })
        return tl;
    }

    public getHidePeriscopeNearWin():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(this.glowFrame.hide(0.1));
        return tl;
    }

    public darken(duration:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(this.darkenPlate.show(duration));
        return tl;
    }

    public unDarken(duration:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(this.darkenPlate.hide(duration));
        return tl;
    }

    public addAvalancheSymbol(ypos:number, symbolName:string):SimpleReelModelSymbolData {
        const visibleIndex = this.model.visibleIndexToRealIndex(0);
        const symbol:SimpleReelModelSymbolData = {
            name:symbolName,
            positionable:this.model._delegate.createSymbol(symbolName, SimpleReelModelState.IDLE, {
                reelID:this.model.id,
                position:ypos
            }),
            reelPosition:ypos,
            reelId:this.model.id
        }
        this.model.symbols.splice(visibleIndex, 0, symbol);
        return symbol;
    }

    public removeAvalancheSymbol(ypos:number) {
        const visibleIndex = this.model.visibleIndexToRealIndex(ypos);
        const symbol = this.model.symbols.splice(visibleIndex, 1)[0];
        this.model._delegate.removeSymbol(symbol, SimpleReelModelState.IDLE);
    }

    public replaceAllPaddingSymbolsBefore(reelData:string[], destroyOriginal:boolean = true):SimpleReelModelSymbolData[] {

        const oldSymbols = this.model.symbols.splice(this.model.paddingConfig.visualPaddingBefore, this.model.paddingConfig.paddingBefore);

        const newSymbols:SimpleReelModelSymbolData[] = [];
        for (let i:number = 0; i < oldSymbols.length; i++) {
            const oldSym = oldSymbols[i];
            const newSymbolName = reelData[i];
            const newSymbolData:SimpleReelModelSymbolData = {
                name:newSymbolName,
                positionable:this.model._delegate.createSymbol(newSymbolName, SimpleReelModelState.IDLE, {
                    reelID:this.model.id,
                    position:oldSym.reelPosition
                }),
                reelPosition:oldSym.reelPosition,
                reelId:this.model.id
            }

            newSymbols.push(newSymbolData);
        }


        if (destroyOriginal) {
            for (let symbol of oldSymbols) {
                this.model._delegate.removeSymbol(symbol, SimpleReelModelState.IDLE);
            }
        }

        this.model.symbols.splice(this.model.paddingConfig.visualPaddingBefore, 0, ...newSymbols);
        return newSymbols;
    }

    public replaceVisibleSymbol(ypos:number, newSymbolName:string, destroyOriginal:boolean = true):SimpleReelModelSymbolData {
        const visibleIndex = this.model.visibleIndexToRealIndex(ypos);
        const symbol = this.model.symbols.splice(visibleIndex, 1)[0];

        if (destroyOriginal) {
            this.model._delegate.removeSymbol(symbol, SimpleReelModelState.IDLE);
        }

        const newSymbolData:SimpleReelModelSymbolData = {
            name:newSymbolName,
            positionable:this.model._delegate.createSymbol(newSymbolName, SimpleReelModelState.IDLE, {
                reelID:this.model.id,
                position:ypos
            }),
            reelPosition:ypos,
            reelId:this.model.id
        }


        this.model.symbols.splice(visibleIndex, 0, newSymbolData);
        return newSymbolData;
    }

    public collapseAvalancheSymbol(ypos:number, amount:number) {
        const visibleIndex = this.model.visibleIndexToRealIndex(ypos);
        const symbol = this.model.symbols[visibleIndex];
        symbol.reelPosition += amount;
    }

    public createSymbol(symbolName:string, state:SimpleReelModelState, symbolPosition:SimpleReelModelSymbolPosition):Positionable {
        const symbol:DasUBootSymbol = SymbolUtils.createSymbol(this, symbolName, state, symbolPosition);

        let addInBack = false;
        if (this.isGrown) {
            const tl = symbol.resize(0.75, 0, 0);
/*            if (isPeriscopeWildSymbol(symbol)){
                tl.add(()=>symbol.unMask());
                addInBack = true;
            }*/
        }
        symbol.position.set(0, symbolPosition.position * DasUBootStatics.symbolHeight);



        if (this.specialSymbols.indexOf(symbolName) >= 0) {
            this.normalOverLayer.addChild(symbol.view);
        } else {
            if (addInBack){
                this.normalLayer.addChildAt(symbol.view,0);
            }else {
                this.normalLayer.addChild(symbol.view);
            }

        }

        return symbol;
    }

    public removeSymbol(symbolData:SimpleReelModelSymbolData, state:SimpleReelModelState):void {
        (<DasUBootSymbol>symbolData.positionable).destroy({children:true});
    }


    public start() {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.normalLayer, {duration:1, alpha:0.6, ease:"power3.out"}));
        this.spinAnimation = tl;
    }

    private stop() {
        this.spinAnimation.kill();
        gsap.to(this.normalLayer, {duration:0.1, alpha:1});
    }

    public commitOverlayToReel(data:SimpleReelModelSymbolData) {
        const index = this.model.visibleIndexToRealIndex(data.reelPosition);
        const realSymbol = this.model.symbols[index];

        if (data.reelPosition != realSymbol.reelPosition) {
            Logger.warn("Sticky symbol positions has incorrect index");
        }

        if (data == realSymbol){
            return;
        }


        const deletedSymbol:SimpleReelModelSymbolData = this.model.symbols.splice(index, 1, data)[0];
        this.removeSymbol(deletedSymbol, SimpleReelModelState.STOPPED);

    }

    private removeIllegalSymbolsOutsideOfVisibleReelArea() {
        let reel = DasUBoot.reelView.reelModels[2];
        for (let symbol of reel.symbols){
            if (symbol.name == "S"){
                if (symbol.reelPosition < 0 || symbol.reelPosition > 3 ){
                    this.replaceVisibleSymbol(symbol.reelPosition, "L5", true);
                }
            }
            if (symbol.name == "W"){
                if (symbol.reelPosition < 0 || symbol.reelPosition > 3 ){
                    this.replaceVisibleSymbol(symbol.reelPosition, "L5", true);
                }
            }
        }



    }
}